import React from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ChevronRight, AlertCircle, Gift } from "lucide-react";
import type { HomeBannersState } from "../hooks/useHomeBanners";
import { estadoDeCreditos } from "../lib/creditsStatus";
import { TiendaNubeIcon } from "./TiendaNubeIcon";
import { TiendaNubeConnectModal } from "./TiendaNubeConnectModal";
import CuestionarioModal from "./CuestionarioModal";

/**
 * Render de los banners de home (prueba gratis / describir la marca). Es
 * presentacional: recibe el `state` resuelto por `useHomeBanners` (que se llama
 * UNA vez por pantalla) para no duplicar el fetch cuando se rendea en mobile y
 * desktop a la vez.
 *
 * El diseño es el mismo que tenía la pantalla clásica de selección de productos
 * (banner responsive: stack en mobile, fila en desktop).
 */
export const HomeBannersView: React.FC<{
  state: HomeBannersState;
  /** Clases extra para el contenedor (ej. `sm:hidden` / `hidden sm:block`). */
  className?: string;
  /** Quita el margen inferior del banner (para layouts en fila, no apilados). */
  noMargin?: boolean;
  /** Si true, muestra el banner de ESTADO de la suscripción (cancelación
   *  programada / pausada) con prioridad sobre los promocionales. Hoy solo se
   *  pasa en las instancias mobile. */
  showStatus?: boolean;
}> = ({ state, className, noMargin, showStatus }) => {
  const navigate = useNavigate();
  const {
    kind,
    freeImagesRemaining,
    creditsTotal,
    discountPercent,
    idCompany,
    status,
    cancelDate,
    resumeDate,
    cancelTrigger,
  } = state;

  const mb = noMargin ? "" : "mb-4";
  // Cancelación por rechazo de pago (fondos insuficientes): copy distinto.
  const byPaymentFailure = cancelTrigger === "fondos_insuficientes";

  // Modal Conectar Tiendanube (banner "tiendanube"): mismo flujo que Mi marca,
  // sin navegar. Estado local por instancia (solo la visible recibe el click).
  const [showTnConnect, setShowTnConnect] = React.useState(false);
  const [tnConnecting, setTnConnecting] = React.useState(false);
  // Modal del cuestionario (banner "cuestionario"). Mismo patrón que el de
  // Tiendanube: estado local por instancia — `HomeBannersView` se rendea dos
  // veces por pantalla (mobile + desktop) y solo la visible recibe el click.
  const [showCuestionario, setShowCuestionario] = React.useState(false);
  // El banner se baja al CERRAR el modal, no al enviar: `dismissCuestionario`
  // pone `kind` en null y este componente hace `return null` cuando no hay
  // kind, lo que desmontaría el modal antes de que el usuario llegue a ver la
  // pantalla de "¡Gracias!". Un ref evita depender del timing del re-render.
  const cuestionarioDoneRef = React.useRef(false);
  const handleTnConnect = async () => {
    if (tnConnecting) return;
    setTnConnecting(true);
    try {
      await state.connectTiendanube?.(); // al éxito el hook oculta el banner
      setShowTnConnect(false);
    } catch {
      /* cancelado / popup bloqueado → queda como estaba */
    } finally {
      setTnConnecting(false);
    }
  };

  // ── Banner de estado de suscripción (prioridad sobre los promocionales) ──
  // Cancelación programada (ámbar) — espeja la vista del Paywall.
  if (showStatus && status === "cancelled") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-amber-50 border border-amber-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-12 sm:pb-0">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
              <h3 className="text-[15px] font-semibold text-brand-black/90">
                Cancelación programada
              </h3>
            </div>
            <p className="text-[15px] text-[#3C3C43]/60">
              Tu plan se cancelará el{" "}
              <strong className="font-medium text-brand-black/80">
                {cancelDate}
              </strong>
              {byPaymentFailure
                ? " por rechazo de tu último pago."
                : ". No se te realizará ningún cobro adicional."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className="absolute bottom-4 right-4 sm:relative sm:bottom-auto sm:right-auto inline-flex items-center gap-1.5 h-10 px-4 bg-white border border-neutral-200 text-brand-black/90 rounded-full shadow-sm text-[15px] font-medium hover:bg-neutral-50 transition-colors flex-shrink-0"
          >
            Ver detalles
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }
  // Suscripción pausada (azul) — espeja la vista del Paywall.
  if (showStatus && status === "paused") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-blue-50 border border-blue-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-12 sm:pb-0">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-blue-600 shrink-0" />
              <h3 className="text-[15px] font-semibold text-brand-black/90">
                Suscripción pausada
              </h3>
            </div>
            <p className="text-[15px] text-[#3C3C43]/60">
              Tu plan se reanudará el{" "}
              <strong className="font-medium text-brand-black/80">
                {resumeDate}
              </strong>
              . No se te cobrará durante la pausa.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className="absolute bottom-4 right-4 sm:relative sm:bottom-auto sm:right-auto inline-flex items-center gap-1.5 h-10 px-4 bg-white border border-neutral-200 text-brand-black/90 rounded-full shadow-sm text-[15px] font-medium hover:bg-neutral-50 transition-colors flex-shrink-0"
          >
            Ver detalles
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (kind === null) return null;

  if (kind === "upsell") {
    // Tres estados, de más a menos urgente (ADR-2026-07-30-banner-home-habla-descuento-real). El corte de "se está
    // quedando sin" ya no es un `<= 3` fijo: sale del mismo criterio que la barra
    // superior —5% de la capacidad del plan— para que la app no tenga dos
    // umbrales distintos para la misma idea (ADR-2026-07-30-solo-criterio-saldo-toda-app).
    const estado = estadoDeCreditos(freeImagesRemaining, creditsTotal);
    const sinCreditos = estado === "sin";
    const urgente = estado !== "ok";
    const off = discountPercent !== null ? `${discountPercent}% OFF` : null;

    // Estructura del banner de referencia: gancho en bold + oferta en bold + un
    // CTA que repite la oferta. Dos anclas y nada más — es una sola línea.
    const gancho = sinCreditos
      ? "TE QUEDASTE SIN CRÉDITOS."
      : freeImagesRemaining === 1
        ? "TE QUEDA 1 CRÉDITO."
        : `TE QUEDAN ${freeImagesRemaining} CRÉDITOS.`;
    const remate = sinCreditos
      ? "y seguí creando sin cortes."
      : "y no frenes tu producción.";

    return (
      <div className={className}>
        <div
          className={`${mb} bg-brand-primary rounded-lg p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <p
            className={`text-[15px] text-white/90 sm:pb-0 ${urgente ? "mb-3 sm:mb-0" : "pb-12"}`}
          >
            {urgente ? (
              <>
                <span className="font-bold uppercase">{gancho}</span>{" "}
                {off ? (
                  <>
                    Suscribite hoy con{" "}
                    <span className="font-bold">{off}</span> {remate}
                  </>
                ) : (
                  // Sin cupón vigente no hay descuento que prometer: el banner
                  // empuja al plan sin inventar un número.
                  <>Suscribite a un plan {remate}</>
                )}
              </>
            ) : (
              "¿Te está gustando Atlas? Suscribite a un plan y conocé todas sus funciones."
            )}
          </p>
          {/* En mobile, cuando el estado es urgente el CTA va full-width debajo
              del texto (como en el clásico); si no, flota abajo a la derecha.
              En desktop siempre es inline a la derecha. */}
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className={`inline-flex items-center justify-center gap-2 h-10 px-4 bg-white hover:bg-white/90 text-brand-primary text-[15px] font-medium rounded-md shadow-sm transition-colors flex-shrink-0 sm:relative sm:bottom-auto sm:right-auto sm:w-auto ${
              urgente ? "w-full" : "absolute bottom-4 right-4"
            }`}
          >
            {urgente ? (off ? `Aprovechar ${off}` : "Ver planes") : "Ver planes"}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // kind === "cuestionario": encuesta de feedback con premio de 10 imágenes.
  // Plan pago + ≥2 semanas de antigüedad + sin responder (ver useHomeBanners).
  // El markup es el que tenía la pantalla clásica cuando el banner vivía suelta
  // ahí, así que para el usuario del clásico no cambia nada; V2 lo hereda.
  if (kind === "cuestionario") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-brand-primary rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-12 sm:pb-0 flex items-center gap-2">
            <Gift className="w-4 h-4 text-white shrink-0" />
            <h3 className="text-[15px] font-semibold text-white">
              Obtené 10 imágenes gratis extra
            </h3>
          </div>
          <button
            type="button"
            onClick={() => setShowCuestionario(true)}
            className="absolute bottom-4 right-4 sm:relative sm:bottom-auto sm:right-auto inline-flex items-center gap-2 h-10 px-4 bg-white hover:bg-white/90 text-brand-primary text-[15px] font-medium rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-colors flex-shrink-0"
          >
            Reclamar
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        {idCompany && (
          <CuestionarioModal
            open={showCuestionario}
            idCompany={idCompany}
            onClose={() => {
              setShowCuestionario(false);
              if (cuestionarioDoneRef.current) state.dismissCuestionario?.();
            }}
            onSubmitted={() => {
              cuestionarioDoneRef.current = true;
            }}
          />
        )}
      </div>
    );
  }

  // kind === "tiendanube": aparece tras completar "Mejorá tus fotos", si la
  // empresa no tiene tienda conectada y el user no lo descartó.
  // Container con borde explícito (bg-brand-primary/[0.06] no compila — ver
  // memoria Tailwind del proyecto — así que el banner quedaba "flotando").
  if (kind === "tiendanube") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-white border border-neutral-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-14 sm:pb-0">
            <div className="flex items-center gap-2 mb-1">
              <TiendaNubeIcon className="h-4 w-auto text-[#2c3357]" />
              <h3 className="text-[15px] font-semibold text-brand-black/90">
                Conectá tu Tienda Nube
              </h3>
            </div>
            <p className="text-[15px] text-[#3C3C43]/60">
              Importá tus productos y creá contenido para tu tienda en segundos.
            </p>
          </div>
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-end gap-2 sm:relative sm:bottom-auto sm:left-auto sm:right-auto sm:w-auto">
            <button
              type="button"
              onClick={() => state.dismissTiendanube?.()}
              className="inline-flex items-center justify-center h-10 px-3 text-[15px] font-medium text-[#3C3C43]/60 hover:text-brand-black/80 rounded-full transition-colors flex-shrink-0"
            >
              No me interesa
            </button>
            <button
              type="button"
              onClick={() => setShowTnConnect(true)}
              className="inline-flex items-center gap-2 h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary flex-shrink-0"
            >
              Conectar
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* companyHasStores={false}: el banner solo aparece si la empresa NO
            tiene NINGUNA tienda conectada (ver useHomeBanners). */}
        <TiendaNubeConnectModal
          open={showTnConnect}
          companyHasStores={false}
          connecting={tnConnecting}
          onCancel={() => setShowTnConnect(false)}
          onConnect={handleTnConnect}
        />
      </div>
    );
  }

  // kind === "brand"
  return (
    <div className={className}>
      <div
        className={`${mb} bg-brand-primary/[0.06] rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
      >
        <div className="pb-12 sm:pb-0">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-brand-primary" />
            <h3 className="text-[15px] font-semibold text-brand-black/90">
              Mejorá tus fotos
            </h3>
          </div>
          <p className="text-[15px] text-[#3C3C43]/60">
            Contale a la IA sobre tu marca para que genere fotos adaptadas a su
            estilo
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/redes-sociales/descripcion-brand")}
          className="absolute bottom-4 right-4 sm:relative sm:bottom-auto sm:right-auto inline-flex items-center gap-2 h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
        >
          Mejorar fotos
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
